import type { FakeItem } from '../data/types';
import { headlineScaleStyle } from './headlineScale';

/* The fake page itself, with no stamp and no keyboard handling — so the builder preview and
   the recording surface render from ONE component and cannot drift apart. FakeReveal wraps
   this with the stamp overlay and the Esc/Back behaviour.

   ── Layout ruling (Lucky, 2026-08-10) ──
   Headline on top, image UNDER it, and the image gets every pixel left over. One layout, always
   — no special full-bleed mode. Earlier versions capped the image at 42% of the frame height,
   which threw away most of a finished graphic and read as if the upload had half-worked.

   The image is sized by flex rather than by a fixed height: headline and caption take what
   they need, the picture takes the rest. */

interface Props {
  item: FakeItem;
  /** Round-level corner banner. Empty string means the round has it switched off. */
  ribbonLabel?: string;
  /** Round-level headline size; combined with an auto step-down for long headlines. */
  headlineSize?: string;
  /** Round-level: show the "Adorian" wordmark. */
  showWordmark?: boolean;
  /** Renders at preview scale in the builder; full-screen on the recording surface. */
  preview?: boolean;
  onImageError?: () => void;
  missing?: boolean;
}

export default function FakePage({
  item, ribbonLabel = '', headlineSize, showWordmark = true, preview = false, onImageError, missing = false,
}: Props) {
  const cls = `fake-page${preview ? ' is-preview' : ''}`;
  const scale = headlineScaleStyle(headlineSize, item.headline);
  const showRibbon = Boolean(ribbonLabel.trim());
  // Nothing in the top corners to clear, so the body reclaims that reserved band.
  const chromeClear = !showRibbon && !showWordmark;

  if (missing) {
    return (
      <div className={`${cls} missing`} style={scale} data-testid="graphic-missing">
        <div className="note">Graphic not found</div>
        <div className="hl">{item.headline}</div>
        <div className="note">This headline is FAKE · upload an image on this item</div>
      </div>
    );
  }

  return (
    <div
      className={`${cls} composed${item.images.length ? ' has-image' : ''}${chromeClear ? ' chrome-clear' : ''}`}
      style={scale}
    >
      {showRibbon && <div className="fake-ribbon">{ribbonLabel}</div>}
      {showWordmark && <div className="fake-wordmark">Adorian</div>}

      <div className="fake-body">
        <div className="fake-headline">{item.headline}</div>

        {item.images.length > 0 && (
          <div className={`fake-images count-${Math.min(item.images.length, 6)}`}>
            {item.images.map((url, i) => (
              <img
                key={`${url}-${i}`}
                src={url}
                alt={i === 0 ? 'In-house graphic for the fabricated headline' : ''}
                onError={onImageError}
              />
            ))}
          </div>
        )}

        {item.body.trim() && <p className="fake-copy">{item.body}</p>}
        {item.footerNote.trim() && <div className="fake-footer">{item.footerNote}</div>}
      </div>
    </div>
  );
}
